import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardBody,
  VStack,
  Text,
  Button,
  HStack,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from '@chakra-ui/react';
import { useAuth } from '../context/AuthContext';
import BundlingProductTab from '../components/BundlingProductTab';
import GoersManualPool from '../components/GoersManualPool';
import IeemWorkspaceTabs from '../components/IeemWorkspaceTabs';
import EmptyStateBlock from '../components/bundling/EmptyStateBlock';
import PageHeader from '../components/bundling/PageHeader';
import IdmIntegrationData from './IdmIntegrationData';
import GoersContentBrowser from '../components/GoersContentBrowser';
import GoersTicketInventory from '../components/GoersTicketInventory';

const DestinationManagement = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedRole, user } = useAuth();
  
  // Determine active tab based on route - hanya untuk IEEM sub menu
  const isIeemRoute = location.pathname.includes('/destination-management/ieem') || location.pathname === '/admin/destination-management/ieem';
  const isBundlingProductRoute = location.pathname.includes('/destination-management/bundling-product') || location.pathname === '/admin/destination-management/bundling-product';
  const isIntegrationDataRoute = location.pathname.includes('/destination-management/integrationData');
  const isGoersContentRoute = location.pathname.includes('/destination-management/goers-content') || location.pathname === '/admin/destination-management/goers-content';
  
  // Tab hanya untuk IEEM: How to Publish dan List Product
  const [activeIeemTab, setActiveIeemTab] = useState(0);
  const [importedProductDraft, setImportedProductDraft] = useState(null);
  
  // Update active tab when route changes
  useEffect(() => {
    if (location.pathname === '/admin/destination-management/ieem' || location.pathname === '/admin/destination-management') {
      // Default redirect to IEEM if no sub route specified
      navigate('/admin/destination-management/ieem', { replace: true });
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    const sourceProduct = location.state?.importedIdmProduct;
    if (!sourceProduct) return;

    setImportedProductDraft(sourceProduct);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate]);

  // Check user role
  const userRole = user?.role || '';
  let parsedUserRoles = [];
  if (userRole) {
    if (Array.isArray(userRole)) {
      parsedUserRoles = userRole;
    } else if (typeof userRole === 'string') {
      if (userRole.includes(';')) {
        parsedUserRoles = userRole.split(';').map(r => r.trim()).filter(r => r);
      } else if (userRole.includes(',')) {
        parsedUserRoles = userRole.split(',').map(r => r.trim()).filter(r => r);
      } else if (userRole.trim()) {
        parsedUserRoles = [userRole.trim()];
      }
    }
  }
  
  const hasRoleIeem = selectedRole === 'ieem' || (!selectedRole && parsedUserRoles.includes('ieem'));
  const hasRoleBp = selectedRole === 'bp' || (!selectedRole && parsedUserRoles.includes('bp'));

  // Mapping IEMMPUBLISH_MEMBER
  const getMemberCode = () => 'twc';

  const handleImportProductFromIdm = React.useCallback((product) => {
    setImportedProductDraft(product);
    navigate('/admin/destination-management/bundling-product', {
      state: { importedIdmProduct: product },
    });
  }, [navigate]);

  const handleImportedProductConsumed = React.useCallback(() => {
    setImportedProductDraft(null);
  }, []);

  const eyebrow = isIeemRoute
    ? 'IEEM Workspace'
    : isBundlingProductRoute
      ? 'Product Workspace'
      : isIntegrationDataRoute
        ? 'Product Workspace'
        : isGoersContentRoute
          ? 'GOERS Content'
          : 'Domain Workspace';

  return (
    <Box>
      <VStack align="stretch" spacing={4} mb={6}>
        <PageHeader
          eyebrow={eyebrow}
          title="Injourney Destination Management"
          description="Kelola channel publishing IEEM dan bundling product untuk Injourney Destination Management."
        />
        <HStack>
          {!isIntegrationDataRoute && !isGoersContentRoute ? (
            <>
              <Button
                size="sm"
                colorScheme="blue"
                variant={isBundlingProductRoute ? 'solid' : 'outline'}
                onClick={() => navigate('/admin/destination-management/bundling-product')}
              >
                List Product PRIME
              </Button>
              <Button
                size="sm"
                colorScheme="blue"
                variant="outline"
                onClick={() => navigate('/admin/destination-management/integrationData')}
              >
                Import dari IDM API
              </Button>
              <Button
                size="sm"
                colorScheme="blue"
                variant="outline"
                onClick={() => navigate('/admin/destination-management/goers-content')}
              >
                GOERS Content
              </Button>
            </>
          ) : (
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate('/admin/destination-management/bundling-product')}
            >
              Kembali ke List Product PRIME
            </Button>
          )}
        </HStack>
        <Text fontSize="sm" color="gray.500">
          List Product PRIME berisi produk yang sudah tersimpan di inventory. Produk dari IDM API dipakai sebagai
          sumber import untuk mengisi form awal, lalu tetap perlu direview sebelum disimpan.
        </Text>
      </VStack>

      <Card bg="white" borderRadius="16px" boxShadow="sm">
        <CardBody p={{ base: 2, lg: 4 }}>
          {isIntegrationDataRoute ? (
            <IdmIntegrationData onImportProduct={handleImportProductFromIdm} />
          ) : isGoersContentRoute ? (
            <Tabs colorScheme="blue" variant="enclosed" isLazy>
              <TabList>
                <Tab>Browse Content</Tab>
                <Tab>Ticket Inventory</Tab>
              </TabList>
              <TabPanels>
                <TabPanel px={0}><GoersContentBrowser /></TabPanel>
                <TabPanel px={0}><GoersTicketInventory /></TabPanel>
              </TabPanels>
            </Tabs>
          ) : isIeemRoute && hasRoleIeem ? (
            /* Hanya tampilkan tab jika di route IEEM */
            <IeemWorkspaceTabs
              activeIeemTab={activeIeemTab}
              onTabChange={setActiveIeemTab}
              memberCode={getMemberCode()}
              productsContent={
                <EmptyStateBlock
                  title="Products workspace akan segera tersedia"
                  description="Daftar product readiness untuk InJourney Destination Management akan ditampilkan di sini."
                />
              }
              analyticsContent={
                <EmptyStateBlock
                  title="Analytics workspace belum tersedia"
                  description="Summary analytics dan exception readiness Destination Management akan muncul setelah data source terhubung."
                />
              }
            />
          ) : isBundlingProductRoute && hasRoleBp ? (
            // Bundling Product route — TWC's own event ticket pool, scoped by member_code
            // so ITDC (MotoGP) tickets never appear here.
            <>
              <GoersManualPool memberId={getMemberCode()} />
              <BundlingProductTab
                memberId={getMemberCode()}
                importedProduct={importedProductDraft}
                onImportedProductConsumed={handleImportedProductConsumed}
              />
            </>
          ) : (
            // Default - redirect ke IEEM
            <VStack align="stretch" spacing={4}>
              <Text color="gray.600">
                Memuat...
              </Text>
            </VStack>
          )}
        </CardBody>
      </Card>
    </Box>
  );
};

export default DestinationManagement;
